import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    OutlinedInput,
    Select,
    TablePagination,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { RootState } from '../../reducers';
import { DataGrid, GridColDef, GridSortDirection } from '@mui/x-data-grid';
import { localDateFormat } from '../../utils/timeFormat';
import {
    fetchMembersPagination,
    fetchMemberStatuses,
    fetchRiskLevels,
    setMemberSearch,
    setSelectedMemberStatus,
    setSelectedRiskLevel,
    setSelectedTeam,
    setSort,
    setIncludeArchivedMembers,
} from '../../store/members.slice';
import { Stack } from '@mui/material';
import Loading from '../Shared/Loading';
import { parentUrl } from '../../utils/constants';
import { Search } from '@mui/icons-material';
import { MemberStatuses } from '../../utils/mappings';
import { isDefaultDate } from '../../utils/common';

const MembersListPage = () => {
    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'RiskScore',
            headerName: 'Risk',
            width: 80,
            sortable: false,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            width: 100,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
            sortable: false,
        },
        {
            field: 'HealthPlan',
            headerName: 'Contracting Health Plan',
            minWidth: 200,
            flex: 1,
            sortable: false,
        },
        {
            field: 'LastSeen',
            headerName: 'Last Seen',
            width: 120,
            valueGetter: (value, row) => `${isDefaultDate(row.LastSeen) ? '-' : localDateFormat(new Date(row.LastSeen))}`,
            sortable: false,
        },
        {
            field: 'Address1',
            headerName: 'Address',
            minWidth: 200,
            flex: 1,
            sortable: false,
        },
        {
            field: 'City',
            headerName: 'City',
            width: 180,
            sortable: false,
        },
        {
            field: 'State',
            headerName: 'State',
            width: 80,
            sortable: false,
        },
        {
            field: 'ZipCode',
            headerName: 'ZipCode',
            width: 80,
            sortable: false,
        },
        {
            field: 'Status',
            headerName: 'Member Status',
            width: 200,
            sortable: false,
        },
    ];

    // const [localMembers, setLocalMembers] = useState([]);
    const dispatch = useAppDispatch();
    const { members, memberSearch, memberStatuses, riskLevels, selectedRiskLevel, selectedMemberStatus, selectedTeam, includeArchivedMembers, sort } =
        useSelector((state: RootState) => state.members);
    const { teams } = useSelector((state: RootState) => state.teams);

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
    const [pageTotal, setPageTotal] = useState(0);

    const resetToFirstPage = useCallback(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, []);

    const handleUserPageChange = useCallback((event: unknown, page: number) => {
        setPaginationModel((prev) => ({
            ...prev,
            page: Math.max(page, 0),
        }));
    }, []);

    const changeSearch = (event: any) => {
        if (!event.key || event.key === 'Enter') {
            resetToFirstPage();
        }
    };

    const prepareStatuses = () => {
        return [
            {
                Name: 'Open',
                List: memberStatuses
                    .filter((s) => MemberStatuses.some((os) => String(os.Id) === String(s.value) && os.IsOpen))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },
            {
                Name: 'Closed',
                List: memberStatuses
                    .filter((s) => MemberStatuses.some((os) => String(os.Id) === String(s.value) && !os.IsOpen))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            },
        ];
    };

    const loadMembers = useCallback(
        async (model: { page: number; pageSize: number }) => {
            const result = await dispatch(fetchMembersPagination(model.pageSize, model.page) as any);
            if (result) {
                setPageTotal(result.totalCount ?? 0);
                if (typeof result.resolvedPageNumber === 'number' && result.resolvedPageNumber !== model.page) {
                    setPaginationModel((prev) => ({ ...prev, page: result.resolvedPageNumber }));
                }
            }
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(fetchRiskLevels());
        dispatch(fetchMemberStatuses());
    }, [dispatch]);

    useEffect(() => {
        loadMembers(paginationModel);
    }, [loadMembers, paginationModel]);
    return (
        <Box>
            {<Loading message={'Loading...'} />}
            <Grid container spacing={3} style={{ padding: '16px 16px 16px 16px' }}>
                <Grid size={12} style={{ paddingBottom: '0px' }} justifyItems="space-between">
                    <FormControl style={{ minWidth: '300px', margin: '0px' }}>
                        <InputLabel htmlFor="outlined-adornment-password">Search</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password"
                            type="text"
                            value={memberSearch}
                            onChange={(e) => {
                                dispatch(setMemberSearch(e.target.value as string));
                            }}
                            onKeyUp={(event: any) => {
                                changeSearch(event);
                            }}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton aria-label="Search" onClick={changeSearch} edge="end">
                                        <Search />
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Search"
                        />
                    </FormControl>
                    <FormControlLabel
                        style={{ paddingLeft: '20px', paddingTop: '10px' }}
                        label={
                            <Box component="div" fontSize={14}>
                                Include Archived Members
                            </Box>
                        }
                        control={
                            <Checkbox
                                checked={includeArchivedMembers}
                                onChange={() => {
                                    dispatch(setIncludeArchivedMembers(!includeArchivedMembers));
                                    resetToFirstPage();
                                }}
                            />
                        }
                    />
                    <Stack direction="row" className="float-right" spacing={2}>
                        <FormControl className="form-control-200">
                            <InputLabel id="demo-simple-select-label">Risk Level</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Risk Level"
                                value={selectedRiskLevel}
                                onChange={(e) => {
                                    dispatch(setSelectedRiskLevel(e.target.value as []));
                                    resetToFirstPage();
                                }}
                            >
                                <MenuItem value={'-1'}>Show All</MenuItem>
                                {riskLevels.map((risk) => {
                                    return (
                                        <MenuItem key={risk.value} value={risk.value}>
                                            {risk.label}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <FormControl className="form-control-200">
                            <InputLabel id="demo-simple-select-label">Teams</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Teams"
                                value={selectedTeam}
                                onChange={(e) => {
                                    dispatch(setSelectedTeam(e.target.value as string));
                                    resetToFirstPage();
                                }}
                            >
                                <MenuItem value={'-1'}>Show All</MenuItem>
                                {teams.map((team, index) => {
                                    return (
                                        <MenuItem key={team.Id} value={team.Name}>
                                            {team.Name}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: '120px' }}>
                            <InputLabel id="demo-simple-select-label">Member Status</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Member Status"
                                value={selectedMemberStatus}
                                multiple
                                onChange={(e) => {
                                    dispatch(setSelectedMemberStatus(e.target.value as []));
                                }}
                                onClose={resetToFirstPage}
                            >
                                <MenuItem value={'-1'}>Show All</MenuItem>
                                {prepareStatuses().map((statusByType, index) => [
                                    <ListSubheader key={index}>{statusByType.Name}</ListSubheader>,
                                    statusByType.List.map((status) => {
                                        return (
                                            <MenuItem key={status.value} value={status.value} sx={{ paddingLeft: 4 }}>
                                                {status.label}
                                            </MenuItem>
                                        );
                                    }),
                                ])}
                            </Select>
                        </FormControl>
                    </Stack>
                </Grid>
                <Grid size={12}>
                    {/* <div style={{ height: '82vh', width: '100%' }}> */}
                    <div style={{ width: '100%' }}>
                        <DataGrid
                            sortModel={sort && sort.field ? [{ field: sort.field, sort: sort.sort as GridSortDirection }] : []}
                            onSortModelChange={(model) => {
                                if (model && model.length > 0 && model[0]) {
                                    dispatch(setSort(model[0]));
                                } else {
                                    // Reset to default sort when sort is cleared
                                    dispatch(setSort({ field: 'LastName', sort: 'asc' }));
                                }
                                resetToFirstPage();
                                resetToFirstPage();
                            }}
                            onCellClick={(params: any) => {
                                window.open(`${parentUrl}provider/members-list/member-chart/${params.id}/member-profile`, '_blank');
                            }}
                            getRowId={(row) => row.Id}
                            rows={members}
                            autoHeight
                            rowCount={pageTotal}
                            columns={columns}
                            paginationModel={paginationModel}
                            paginationMode="server"
                            hideFooterPagination
                            getRowClassName={(params) =>
                                MemberStatuses.some((s) => s.Name === params.row.Status && !s.IsOpen)
                                    ? 'closed-status-color'
                                    : `color${params.row.IsInTransition}`
                            }
                            pageSizeOptions={[15]}
                        />
                    </div>
                    <TablePagination
                        component="div"
                        count={pageTotal}
                        page={paginationModel.page}
                        onPageChange={handleUserPageChange}
                        rowsPerPage={paginationModel.pageSize}
                        rowsPerPageOptions={[paginationModel.pageSize]}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default MembersListPage;
